import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWindowClose } from 'react-icons/fa';
import CommonModal from './CommonModal';
import {
  catchError,
  commonModalSetting,
  formatWon,
  onlyDigits,
} from 'JS/common';
import { refundAdminOrder } from 'JS/API';

const MIN_REASON = 1;
const MAX_REASON = 3000;

const AdminOrderRefundModal = ({ merchantUid, paidAmount, onClose, onRefunded }) => {
  const [mode, setMode] = useState('full'); // 'full' | 'partial'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const [innerAlertBox, setInnerAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });
  const [innerAlert, setInnerAlert] = useState('');
  const navigate = useNavigate();

  const handleBackgroundClick = e => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose();
    }
  };

  const partialAmountNum = Number(amount) || 0;
  const refundAmount = mode === 'full' ? paidAmount : partialAmountNum;
  const remaining =
    mode === 'partial' ? Math.max(0, paidAmount - partialAmountNum) : 0;

  const handleConfirm = async () => {
    // 검증
    if (mode === 'partial') {
      if (!partialAmountNum || partialAmountNum <= 0) {
        return commonModalSetting(
          setInnerAlertBox,
          true,
          'alert',
          '환불 금액을 1원 이상 입력해 주세요.'
        );
      }
      if (partialAmountNum > paidAmount) {
        return commonModalSetting(
          setInnerAlertBox,
          true,
          'alert',
          '환불 금액이 결제 금액을 초과했습니다.'
        );
      }
    }
    const trimmedReason = reason.trim();
    if (
      trimmedReason.length < MIN_REASON ||
      trimmedReason.length > MAX_REASON
    ) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        `환불 사유는 ${MIN_REASON}~${MAX_REASON}자 사이로 입력해 주세요.`
      );
    }

    setLoading(true);
    const result = await refundAdminOrder(merchantUid, {
      amount: mode === 'partial' ? partialAmountNum : undefined,
      reason: trimmedReason,
    });
    setLoading(false);

    if (typeof result === 'object') {
      onRefunded();
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
  };

  return (
    <>
      <div className='modal-background' onClick={handleBackgroundClick}>
        <div className='modal admin-order-refund'>
          <div className='topBar'>
            <h2>환불 처리</h2>
            <div onClick={onClose}>
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div className='wrap'>
            <div className='preview-section'>
              <div className='preview-row'>
                <span className='label'>결제 금액</span>
                <span className='value'>{formatWon(paidAmount)}</span>
              </div>
            </div>

            {/* 환불 유형 */}
            <div className='input-section'>
              <label>환불 유형 *</label>
              <div className='radio-row'>
                <label className='radio-item'>
                  <input
                    type='radio'
                    name='refund-mode'
                    value='full'
                    checked={mode === 'full'}
                    onChange={() => setMode('full')}
                  />
                  <span>전액 환불</span>
                </label>
                <label className='radio-item'>
                  <input
                    type='radio'
                    name='refund-mode'
                    value='partial'
                    checked={mode === 'partial'}
                    onChange={() => setMode('partial')}
                  />
                  <span>부분 환불</span>
                </label>
              </div>
            </div>

            {/* 부분 환불 금액 */}
            {mode === 'partial' && (
              <div className='input-section'>
                <label>환불 금액 *</label>
                <div className='input-row'>
                  <input
                    type='text'
                    inputMode='numeric'
                    value={
                      amount === ''
                        ? ''
                        : Number(amount).toLocaleString('ko-KR')
                    }
                    onChange={e => {
                      const v = onlyDigits(e.target.value);
                      // max는 paid_amount로 제한
                      if (v === '' || v <= paidAmount) setAmount(v);
                      else setAmount(paidAmount);
                    }}
                    placeholder='0'
                  />
                  <span className='suffix'>원</span>
                </div>
                <p className='hint'>
                  잔여 환불 가능액: {formatWon(remaining)}
                </p>
              </div>
            )}

            {/* 사유 */}
            <div className='input-section'>
              <label>환불 사유 *</label>
              <textarea
                rows={4}
                value={reason}
                onChange={e => setReason(e.target.value.slice(0, MAX_REASON))}
                placeholder='환불 사유를 입력해 주세요.'
              />
              <p className='hint'>
                {reason.length} / {MAX_REASON}자 (최소 {MIN_REASON}자)
              </p>
            </div>

            {/* 환불 금액 미리보기 */}
            <div className='preview-section'>
              <div className='preview-row'>
                <span className='label'>환불할 금액</span>
                <span className='value highlight'>
                  {formatWon(refundAmount)}
                </span>
              </div>
            </div>

            <div className='footer'>
              <div className='btn-group'>
                <button
                  className='confirm-btn'
                  onClick={handleConfirm}
                  disabled={loading}>
                  {loading ? '환불 중...' : '환불 확정'}
                </button>
                <button className='cancel-btn' onClick={onClose}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {innerAlertBox.bool && (
        <CommonModal
          setModal={setInnerAlertBox}
          modal={innerAlertBox}
          okFn={() => {
            if (innerAlert === 'logout') navigate('/');
          }}
        />
      )}
    </>
  );
};

export default AdminOrderRefundModal;
